import { useMemo, useState } from "react";

import { AnalyticsEvent } from "@repo/edtech-analytics";
import { CrossIcon, HeartIcon } from "@repo/edtech-assets";
import { useIsMobile } from "@repo/edtech-shared";
import {
  Button,
  ButtonVariant,
  Modal,
} from "@repo/edtech-ui-kit";
import clsx from "clsx";

import { ExtendedLesson, useAnalyticsContext } from "../../../model";
import { StarsRating } from "../stars-rating";
import { getQuestionsByType } from "./lib";
import {
  RATING_MAX,
  Ratings,
  RatingStep,
} from "./model";

import st from "./LessonRating.module.scss";

type Props = {
  lesson: ExtendedLesson,
  // storybook prop
  initialStep?: RatingStep,
  // storybook prop
  initialActiveQuestion?: number,
  // storybook prop
  initialActiveRating?: number | null,
  // storybook prop
  initialModalOpen?: boolean,
  // storybook prop
  initialComment?: string,
};

export const LessonRating = ({
  lesson,
  initialStep = RatingStep.Rate,
  initialActiveQuestion = 0,
  initialActiveRating = null,
  initialModalOpen = false,
  initialComment = "",
}: Props) => {
  const {
    questions,
    questionKeys,
  } = useMemo(() => getQuestionsByType(lesson),
    [ lesson ]);

  const [ ratings, setRatings ] = useState<Ratings>({
    materialRate: 0,
    materialClarity: 0,
    materialInterest: 0,
    materialRelevance: 0,
    materialComment: "",
  });

  const [ activeRating, setActiveRating ] = useState<number | null>(initialActiveRating);
  const [ step, setStep ] = useState<RatingStep>(initialStep);
  const [ activeQuestion, setActiveQuestion ] = useState(initialActiveQuestion);
  const [ comment, setComment ] = useState<string>(initialComment);
  const isMobile = useIsMobile(768);

  const { track } = useAnalyticsContext();

  const currentKey = questionKeys[ activeQuestion ] as keyof Ratings;

  const handleStarClick = (value: number) => {
    setRatings((prev) =>
      ({
        ...prev,
        [ currentKey ]: value,
      }) as Ratings);

    setActiveRating(null);

    if (activeQuestion === questionKeys.length - 1) {
      setStep(RatingStep.AddComment);
    } else {
      setActiveQuestion((prev) => prev + 1);
    }
  };

  const handleHoverChange = (num: number | null) => {
    setActiveRating(num);
  };

  const handleCommentChange = (v: string) => {
    setComment(v);
    setRatings((prev) => ({
      ...prev,
      materialComment: v || "",
    }));
  };

  const handleSubmitRating = () => {
    const payload = Object.fromEntries(Object.entries(ratings).filter(([ _, value ]) => value !== undefined && value !== ""));

    track({
      type: AnalyticsEvent.Rated,
      ...payload,
    });

    setStep(RatingStep.End);
  };

  return (
    <div className={st.rating}>
      {
        step === RatingStep.Rate && (
          <>
            <h2 className={st.title}>{questions[ currentKey ]}</h2>
            <StarsRating
              activeStar={activeRating}
              isMobile={isMobile}
              maxRating={RATING_MAX}
              onHoverChange={handleHoverChange}
              onStarClick={handleStarClick}
            />
          </>
        )
      }

      {
        step === RatingStep.AddComment && (
          <>
            <p className={st.title}>Добавить комментарий?</p>
            <div className={st.comment_buttons}>
              <Modal
                isCloseBtnHidden
                isFullScreenMobile
                isOpen={initialModalOpen}
                Trigger={<button className={st.comment_button}>Добавить</button>}
              >
                {
                  (onClose) => (
                    <div className={st.modal}>
                      <div className={st.top}>
                        <h2 className={st.modal_title}>Что мы можем улучшить?</h2>
                        <button onClick={onClose}>
                          <CrossIcon className={st.close_icon} />
                        </button>
                      </div>
                      <textarea
                        className={st.textarea}
                        placeholder="Комментарий"
                        value={comment}
                        onChange={(e) => handleCommentChange(e.target.value)}
                      />
                      <div className={st.modal_buttons}>
                        {
                          isMobile && (
                            <Button
                              className={st.modal_button}
                              variant={ButtonVariant.Outline}
                              onClick={onClose}
                            >
                              Отмена
                            </Button>
                          )
                        }
                        <Button
                          className={st.modal_button}
                          disabled={!comment}
                          onClick={
                            () => {
                              onClose();
                              handleSubmitRating();
                            }
                          }
                        >
                          Отправить
                        </Button>
                      </div>
                    </div>
                  )
                }
              </Modal>
              <button
                className={st.comment_button}
                onClick={handleSubmitRating}
              >
                Не сейчас
              </button>
            </div>
          </>
        )
      }

      {
        step === RatingStep.End && (
          <div className={st.end}>
            <p className={st.title}>Спасибо за отзыв!</p>
            <HeartIcon className={clsx(st.icon, st.selected)} />
          </div>
        )
      }
    </div>
  );
};
